import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  fetchGuideContent,
  getGuideBySlug,
  getGuideCanonicalUrl,
  getGuideOgImageUrl,
  fetchGuidesManifest,
} from '../services/guides';
import type { GuideMeta } from '../types/guide';

const SITE_NAME = 'GenAnything';

function ArticleJsonLd({ guide, canonicalUrl, ogImageUrl }: { guide: GuideMeta; canonicalUrl: string; ogImageUrl: string }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.metaDescription || guide.description,
    image: ogImageUrl,
    datePublished: guide.publishedAt,
    dateModified: guide.updatedAt,
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: { '@type': 'Organization', name: SITE_NAME, logo: { '@type': 'ImageObject', url: `${typeof window !== 'undefined' ? window.location.origin : ''}/logo-icon.png` } },
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function BreadcrumbJsonLd({ guide, canonicalUrl }: { guide: GuideMeta; canonicalUrl: string }) {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://genanything.ai';
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: origin + '/' },
      { '@type': 'ListItem', position: 2, name: 'Guides', item: origin + '/guides' },
      { '@type': 'ListItem', position: 3, name: guide.title, item: canonicalUrl },
    ],
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function GuidePage() {
  const { slug } = useParams<{ slug: string }>();
  const [guide, setGuide] = useState<GuideMeta | null>(null);
  const [content, setContent] = useState<string>('');
  const [related, setRelated] = useState<GuideMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [heroImageError, setHeroImageError] = useState(false);

  useEffect(() => {
    if (!slug) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const meta = await getGuideBySlug(slug);
        if (cancelled || !meta) {
          setNotFound(true);
          setLoading(false);
          return;
        }
        setGuide(meta);

        const [body, manifest] = await Promise.all([
          fetchGuideContent(slug),
          fetchGuidesManifest(),
        ]);
        if (cancelled) return;
        setContent(body);

        const relatedSlugs = meta.relatedSlugs?.slice(0, 6) ?? [];
        const relatedList = relatedSlugs
          .map((s) => manifest.guides.find((g) => g.slug === s))
          .filter(Boolean) as GuideMeta[];
        setRelated(relatedList);
      } catch {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    setHeroImageError(false);
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="text-white/60 animate-pulse">Loading guide...</div>
      </div>
    );
  }

  if (notFound || !guide) {
    return (
      <div className="min-h-screen bg-bg flex flex-col items-center justify-center gap-4 px-4">
        <h1 className="text-xl font-semibold text-white">Guide not found</h1>
        <Link to="/guides" className="text-white/80 hover:text-white underline">
          View all guides
        </Link>
      </div>
    );
  }

  const canonicalUrl = getGuideCanonicalUrl(guide.slug);
  const ogImageUrl = getGuideOgImageUrl(guide);
  const metaDesc = guide.metaDescription || guide.description;

  return (
    <>
      <Helmet>
        <title>{guide.title} | {SITE_NAME}</title>
        <meta name="description" content={metaDesc} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={guide.title} />
        <meta property="og:description" content={metaDesc} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={guide.title} />
        <meta name="twitter:description" content={metaDesc} />
        <meta name="twitter:image" content={ogImageUrl} />
      </Helmet>
      <ArticleJsonLd guide={guide} canonicalUrl={canonicalUrl} ogImageUrl={ogImageUrl} />
      <BreadcrumbJsonLd guide={guide} canonicalUrl={canonicalUrl} />

      <div className="min-h-screen bg-bg text-white">
        <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <nav className="text-sm text-white/60 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/guides" className="hover:text-white transition-colors">Guides</Link>
            <span className="mx-2">/</span>
            <span className="text-white truncate max-w-[12rem] sm:max-w-none inline-block" title={guide.title}>{guide.title}</span>
          </nav>

          <header className="mb-10">
            <figure className="rounded-2xl overflow-hidden mb-8 bg-white/5 shadow-xl ring-1 ring-white/10 aspect-[1200/630]">
              <img
                src={heroImageError ? `https://picsum.photos/seed/${guide.slug}/1200/630` : ogImageUrl}
                alt=""
                className="w-full h-full object-cover"
                loading="eager"
                decoding="async"
                onError={() => setHeroImageError(true)}
              />
            </figure>
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-white/10 text-white/80 capitalize mb-4">
              {guide.category}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-[2.5rem] font-bold text-white leading-tight tracking-tight">
              {guide.title}
            </h1>
            <p className="mt-4 text-white/75 text-lg leading-relaxed">
              {guide.description}
            </p>
            <div className="mt-5 flex flex-wrap gap-x-4 gap-y-1 text-sm text-white/50">
              <time dateTime={guide.publishedAt}>
                Published {new Date(guide.publishedAt).toLocaleDateString()}
              </time>
              {guide.updatedAt !== guide.publishedAt && (
                <time dateTime={guide.updatedAt}>
                  Updated {new Date(guide.updatedAt).toLocaleDateString()}
                </time>
              )}
            </div>
          </header>

          <div className="text-lg text-white/90 [&>*:first-child]:mt-0">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h2: ({ children }) => (
                  <h2 className="text-xl font-semibold text-white mt-10 mb-4 pb-2 border-b border-white/10 first:mt-0">
                    {children}
                  </h2>
                ),
                h3: ({ children }) => (
                  <h3 className="text-lg font-semibold text-white mt-6 mb-3">
                    {children}
                  </h3>
                ),
                p: ({ children }) => (
                  <p className="mb-5 leading-relaxed">
                    {children}
                  </p>
                ),
                ul: ({ children }) => (
                  <ul className="list-disc pl-6 my-5 space-y-2">
                    {children}
                  </ul>
                ),
                ol: ({ children }) => (
                  <ol className="list-decimal pl-6 my-5 space-y-2">
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li className="leading-relaxed">
                    {children}
                  </li>
                ),
                a: ({ href, children }) => (
                  <a href={href} className="text-white underline hover:no-underline" target={href?.startsWith('http') ? '_blank' : undefined} rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}>
                    {children}
                  </a>
                ),
                strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
              }}
            >
              {content}
            </ReactMarkdown>
          </div>

          {related.length > 0 && (
            <aside className="mt-14 pt-10 border-t border-white/10">
              <h2 className="text-xl font-semibold text-white mb-5">Related guides</h2>
              <ul className="grid gap-4 sm:grid-cols-2">
                {related.map((g) => {
                  const relatedOg = getGuideOgImageUrl(g);
                  return (
                    <li key={g.slug}>
                      <Link
                        to={`/guides/${g.slug}`}
                        className="group flex gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition hover:border-white/20 hover:bg-white/10"
                      >
                        <div className="h-20 w-28 shrink-0 overflow-hidden rounded-lg bg-white/5 ring-1 ring-white/10">
                          <img
                            src={relatedOg}
                            alt=""
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <span className="font-medium text-white group-hover:underline line-clamp-2 text-sm">
                          {g.title}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </aside>
          )}

          <div className="mt-10 pt-6 border-t border-white/10">
            <Link to="/guides" className="text-white/70 hover:text-white text-sm">
              ← All guides
            </Link>
          </div>
        </article>
      </div>
    </>
  );
}
